import type {FC, ReactElement} from 'react';
import PropTypes from 'prop-types';
import {PWMListIcon, CertifyIcon} from 'src/PWM/components/PWMIcons';
import {
  Avatar,
  Box,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Stack,
  Typography
} from '@mui/material';
import {Notifications} from 'pwm-shared/src/types';
import {NotificationType} from 'pwm-shared/src/enums';
import {Scrollbar} from 'src/components/scrollbar';
import {formatDate} from 'src/PWM/utils/date-utils';
import {PWMLink} from '../PWMLink';

const NotificationIcons: {[K in NotificationType]?: ReactElement} = {
  ["Certify Sub List Request"]: <CertifyIcon/>,
  ["Remove Vendor Request"]: <PWMListIcon/>
};

const renderContent = (notification: Notifications.NotificationsResponse): JSX.Element | null => {
  return (
    <>
      <ListItemAvatar sx={{mt: 0.5}}>
        <Avatar>
          {NotificationIcons[notification.type]}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={(
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap'
            }}
          >
            <Typography
              sx={{mr: 0.5}}
              variant="subtitle2"
            >
              {notification.type}
            </Typography>
            <PWMLink
              href={`/notifications/${notification.notificationId}`}
            >
              {notification.name}
            </PWMLink>
          </Box>
        )}
        secondary={(
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {formatDate(notification.created)}
          </Typography>
        )}
        sx={{my: 0}}
      />
    </>
  );
};

interface NotificationsPopoverProps {
  anchorEl: null | Element;
  notifications: any[];
  onClose?: () => void;
  open?: boolean;
}

export const PWMNotificationsPopover: FC<NotificationsPopoverProps> = (props) => {
  const {
    anchorEl,
    notifications,
    onClose,
    open = false,
    ...other
  } = props;

  const isEmpty = notifications.length === 0;

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'left',
        vertical: 'bottom'
      }}
      disableScrollLock
      onClose={onClose}
      open={open}
      PaperProps={{sx: {width: 380}}}
      {...other}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: 3,
          py: 2
        }}
      >
        <Typography
          color="inherit"
          variant="h6"
        >
          Notifications
        </Typography>
      </Stack>
      {
        isEmpty
          ? (
            <Box sx={{p: 2}}>
              <Typography variant="subtitle2">
                There are no notifications
              </Typography>
            </Box>
          )
          : (
            <Scrollbar sx={{maxHeight: 400}}>
              <List disablePadding>
                {notifications.map((notification) => (
                  <ListItem
                    divider
                    key={notification.id}
                    sx={{
                      alignItems: 'flex-start',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      },
                      '& .MuiListItemSecondaryAction-root': {
                        top: '24%'
                      }
                    }}
                  >
                    {renderContent(notification)}
                  </ListItem>
                ))}
              </List>
            </Scrollbar>
          )
      }
    </Popover>
  );
};

PWMNotificationsPopover.propTypes = {
  anchorEl: PropTypes.any,
  notifications: PropTypes.array.isRequired,
  onClose: PropTypes.func,
  open: PropTypes.bool
};
